import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Field } from 'react-final-form';
import { Box, Button, Grid, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useAsyncFn } from 'react-use';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import CustomSelect from 'components/Forms/CustomSelect';
import {
  getCompanyJobRolesCatalog,
  getCompanyTrainingModulesByJobRoles,
  getCompanyTrainingModulesCatalog,
  updateCompanyJobRoles,
  updateCompanyTrainingModules,
} from 'http/companies';
import { actions as modalActions } from 'redux/modal';
import getConfirmationModalConfig from 'utils/getConfirmationModalConfig';
import { generateErrorObject, resolveApiErrorMessage } from 'utils';
import Loader from 'components/Loader';
import useStyles from './styles';
import configErrors from './configErrors';
import EntitlementAccordionList from './EntitlementAccordionList';
import VersionChipGroup from './VersionChipGroup';

const TRAINING_METHODS = ['theory', 'practical', 'ojt'];

const toggleId = (ids, id) => (ids.includes(id) ? ids.filter(value => value !== id) : [...ids, id]);

const collectVersionIds = (roleIds, versionsByRole) =>
  roleIds.flatMap(roleId => versionsByRole[roleId] || []).filter(Boolean);

const isAssignableVersion = item => item?.status !== 'draft';

const filterAssignable = items => (items || []).filter(isAssignableVersion);

const countSelectedAssignable = (items, selectedIds) => {
  const selected = new Set(selectedIds || []);
  return filterAssignable(items).filter(item => selected.has(item.id)).length;
};

const RolesAndTrainingModulesForm = ({ companyId, routesAliases }) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation('common');

  const [jobRoleCatalog, setJobRoleCatalog] = useState([]);
  const [moduleCatalog, setModuleCatalog] = useState([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [selectedVersionIdsByRole, setSelectedVersionIdsByRole] = useState({});
  const [selectedModuleIds, setSelectedModuleIds] = useState([]);
  const [selectedPartIdsByModule, setSelectedPartIdsByModule] = useState({});
  const [manualModuleIds, setManualModuleIds] = useState([]);
  const [requiredModuleIds, setRequiredModuleIds] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [validationErrors, setValidationErrors] = useState({
    jobRoles: null,
    trainingModules: null,
  });
  const manualModuleIdsRef = useRef([]);
  const initialLoadDoneRef = useRef(false);
  const saveErrorsRef = useRef(null);
  const shouldScrollToErrorsRef = useRef(false);

  useEffect(() => {
    manualModuleIdsRef.current = manualModuleIds;
  }, [manualModuleIds]);

  useEffect(() => {
    if (!shouldScrollToErrorsRef.current) {
      return;
    }
    if (!validationErrors.jobRoles && !validationErrors.trainingModules) {
      return;
    }
    shouldScrollToErrorsRef.current = false;
    saveErrorsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [validationErrors]);

  const openErrorModal = useCallback(
    message => {
      const closeModal = () => dispatch(modalActions.closeModal());
      const [config, element] = getConfirmationModalConfig({
        confirmTitle: t('buttons.ok'),
        confirmAction: closeModal,
        rejectAction: closeModal,
        message,
      });
      dispatch(modalActions.openModal(config, element));
    },
    [dispatch, t],
  );

  const selectedVersionIds = useMemo(
    () => collectVersionIds(selectedRoleIds, selectedVersionIdsByRole),
    [selectedRoleIds, selectedVersionIdsByRole],
  );

  const selectedRoles = useMemo(
    () => jobRoleCatalog.filter(role => selectedRoleIds.includes(role.id)),
    [jobRoleCatalog, selectedRoleIds],
  );

  const selectedModules = useMemo(
    () => moduleCatalog.filter(module => selectedModuleIds.includes(module.id)),
    [moduleCatalog, selectedModuleIds],
  );

  const unselectedCatalogVersionsCount = useMemo(
    () =>
      selectedRoles.reduce((sum, role) => {
        const selected = new Set(selectedVersionIdsByRole[role.id] || []);
        const unselected = filterAssignable(role.versions).filter(version => !selected.has(version.id)).length;
        return sum + unselected;
      }, 0),
    [selectedRoles, selectedVersionIdsByRole],
  );

  const unselectedCatalogPartsCount = useMemo(
    () =>
      selectedModules.reduce((sum, module) => {
        const selected = new Set(selectedPartIdsByModule[module.id] || []);
        const unselected = filterAssignable(module.parts).filter(part => !selected.has(part.id)).length;
        return sum + unselected;
      }, 0),
    [selectedModules, selectedPartIdsByModule],
  );

  const addModuleOptions = useMemo(
    () =>
      moduleCatalog
        .filter(module => !selectedModuleIds.includes(module.id))
        .map(module => ({
          value: module.id,
          label: `${module.code}. ${module.name}`,
        })),
    [moduleCatalog, selectedModuleIds],
  );

  const validateForm = useCallback(() => {
    const rolesWithoutVersions = selectedRoleIds.filter(roleId => !(selectedVersionIdsByRole[roleId] || []).length);
    const modulesWithoutParts = selectedModuleIds.filter(moduleId => !(selectedPartIdsByModule[moduleId] || []).length);

    const nextErrors = {
      jobRoles: rolesWithoutVersions.length
        ? t('company.jobRoleVersionsRequired', {
            defaultValue: 'At least one job role version is required for each selected job role',
          })
        : null,
      trainingModules: modulesWithoutParts.length
        ? t('company.trainingPartVersionsRequired', {
            defaultValue: 'At least one part version is required for each selected training module',
          })
        : null,
    };

    setValidationErrors(nextErrors);
    const isValid = !nextErrors.jobRoles && !nextErrors.trainingModules;
    if (!isValid) {
      shouldScrollToErrorsRef.current = true;
    }
    return isValid;
  }, [selectedModuleIds, selectedPartIdsByModule, selectedRoleIds, selectedVersionIdsByRole, t]);

  const mergeSuggestedModules = useCallback(
    async (versionIds, currentManualModuleIds = manualModuleIdsRef.current) => {
      if (!versionIds.length) {
        setRequiredModuleIds([]);
        setSelectedModuleIds(current => current.filter(id => currentManualModuleIds.includes(id)));
        return [];
      }

      setModulesLoading(true);
      try {
        const response = await getCompanyTrainingModulesByJobRoles(companyId, {
          systemJobRoleVersionIds: versionIds,
        });
        if (response?.error) {
          openErrorModal(
            resolveApiErrorMessage(response.message, {
              t,
              fallbackMessage: t('errors.serverError'),
            }),
          );
          return [];
        }

        const suggested = response.trainingModules || [];
        const suggestedIds = suggested.map(item => item.id);

        setModuleCatalog(prev => {
          const byId = new Map(prev.map(item => [item.id, item]));
          suggested.forEach(item => {
            byId.set(item.id, {
              ...(byId.get(item.id) || {}),
              ...item,
              parts: item.parts?.length ? item.parts : byId.get(item.id)?.parts || [],
            });
          });
          return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
        });

        setRequiredModuleIds(suggestedIds);
        setSelectedModuleIds(current => {
          const next = new Set([...current.filter(id => currentManualModuleIds.includes(id)), ...suggestedIds]);
          return Array.from(next);
        });

        setSelectedPartIdsByModule(current => {
          const next = { ...current };
          suggested.forEach(module => {
            if (!next[module.id]) {
              next[module.id] = [];
            }
          });
          Object.keys(next).forEach(moduleId => {
            const id = Number(moduleId);
            if (!suggestedIds.includes(id) && !currentManualModuleIds.includes(id)) {
              delete next[moduleId];
            }
          });
          return next;
        });

        return suggestedIds;
      } finally {
        setModulesLoading(false);
      }
    },
    [companyId, openErrorModal, t],
  );

  const [loadState, loadFormData] = useAsyncFn(async () => {
    const [rolesCatalogResponse, modulesCatalogResponse] = await Promise.all([
      getCompanyJobRolesCatalog(companyId),
      getCompanyTrainingModulesCatalog(companyId),
    ]);

    if (rolesCatalogResponse?.error || modulesCatalogResponse?.error) {
      openErrorModal(
        resolveApiErrorMessage(rolesCatalogResponse?.message || modulesCatalogResponse?.message, {
          t,
          fallbackMessage: t('errors.serverError'),
        }),
      );
      return null;
    }

    const roles = rolesCatalogResponse.jobRoles || [];
    const modules = modulesCatalogResponse.trainingModules || [];

    const nextSelectedRoleIds = roles
      .filter(role => (role.systemJobRoleVersionIds || []).length > 0)
      .map(role => role.id);
    const nextVersionsByRole = {};
    roles.forEach(role => {
      nextVersionsByRole[role.id] = role.systemJobRoleVersionIds || [];
    });

    const assignedModuleIds = modules
      .filter(
        module =>
          (module.systemTrainingPartIds || []).length > 0 ||
          (modulesCatalogResponse.trainingModuleIds || []).includes(module.id),
      )
      .map(module => module.id);
    const nextPartsByModule = {};
    modules.forEach(module => {
      nextPartsByModule[module.id] = module.systemTrainingPartIds || [];
    });

    manualModuleIdsRef.current = assignedModuleIds;
    setJobRoleCatalog(roles);
    setModuleCatalog(modules);
    setSelectedRoleIds(nextSelectedRoleIds);
    setSelectedVersionIdsByRole(nextVersionsByRole);
    setSelectedModuleIds(assignedModuleIds);
    setSelectedPartIdsByModule(nextPartsByModule);
    setManualModuleIds(assignedModuleIds);
    setValidationErrors({ jobRoles: null, trainingModules: null });
    setFormKey(current => current + 1);

    const versionIds = collectVersionIds(nextSelectedRoleIds, nextVersionsByRole);
    if (versionIds.length) {
      await mergeSuggestedModules(versionIds, assignedModuleIds);
    } else {
      setRequiredModuleIds([]);
    }

    return true;
  }, [companyId, mergeSuggestedModules, openErrorModal, t]);

  useEffect(() => {
    initialLoadDoneRef.current = false;
  }, [companyId]);

  useEffect(() => {
    if (initialLoadDoneRef.current) {
      return;
    }
    initialLoadDoneRef.current = true;
    loadFormData();
  }, [companyId, loadFormData]);

  const [submitState, saveAssignments] = useAsyncFn(async () => {
    if (!validateForm()) {
      return undefined;
    }

    const jobRolesPayload = selectedRoles.map(role => ({
      systemJobRoleId: role.id,
      systemJobRoleVersionIds: selectedVersionIdsByRole[role.id] || [],
    }));

    const trainingModulesPayload = selectedModules.map(module => ({
      systemTrainingModuleId: module.id,
      source: requiredModuleIds.includes(module.id) ? 'from_role_requirement' : 'manual',
      systemTrainingPartIds: selectedPartIdsByModule[module.id] || [],
    }));

    // Sequential: job roles first (may provision required modules), then modules/parts.
    const rolesResult = await updateCompanyJobRoles(companyId, {
      jobRoles: jobRolesPayload,
    });
    if (rolesResult.error) {
      const message = rolesResult.message;
      const mappedErrors = generateErrorObject(message, configErrors);
      const resolved = resolveApiErrorMessage(message, {
        t,
        fallbackMessage: t('errors.serverError'),
      });
      openErrorModal(resolved);
      return Object.keys(mappedErrors).length ? mappedErrors : { _error: message };
    }

    const modulesResult = await updateCompanyTrainingModules(companyId, {
      trainingModules: trainingModulesPayload,
    });

    if (modulesResult.error) {
      const message = modulesResult.message;
      const mappedErrors = generateErrorObject(message, configErrors);
      const resolved = resolveApiErrorMessage(message, {
        t,
        fallbackMessage: t('errors.serverError'),
      });
      if (Object.keys(mappedErrors).length > 0) {
        openErrorModal(resolved);
        return mappedErrors;
      }
      openErrorModal(resolved);
      return { _error: message };
    }

    history.push(routesAliases.companiesList);
    return undefined;
  }, [
    companyId,
    history,
    requiredModuleIds,
    routesAliases.companiesList,
    selectedModules,
    selectedPartIdsByModule,
    selectedRoles,
    selectedVersionIdsByRole,
    t,
    validateForm,
  ]);

  const handleAddRole = option => {
    if (!option?.value) {
      return;
    }
    const roleId = option.value;
    if (!selectedRoleIds.includes(roleId)) {
      setSelectedRoleIds(current => [...current, roleId]);
      setSelectedVersionIdsByRole(current => ({
        ...current,
        [roleId]: current[roleId] || [],
      }));
    }
    setValidationErrors(current => ({ ...current, jobRoles: null }));
  };

  const handleRemoveRole = async roleId => {
    const nextRoleIds = selectedRoleIds.filter(id => id !== roleId);
    const nextVersions = { ...selectedVersionIdsByRole };
    delete nextVersions[roleId];
    setSelectedRoleIds(nextRoleIds);
    setSelectedVersionIdsByRole(nextVersions);
    setValidationErrors(current => ({ ...current, jobRoles: null }));
    await mergeSuggestedModules(collectVersionIds(nextRoleIds, nextVersions));
  };

  const handleToggleVersion = async (roleId, versionId) => {
    const nextVersions = {
      ...selectedVersionIdsByRole,
      [roleId]: toggleId(selectedVersionIdsByRole[roleId] || [], versionId),
    };
    setSelectedVersionIdsByRole(nextVersions);
    setValidationErrors(current => ({ ...current, jobRoles: null }));
    await mergeSuggestedModules(collectVersionIds(selectedRoleIds, nextVersions));
  };

  const handleAddAllRoleVersions = async () => {
    const nextSelectedRoleIds = jobRoleCatalog.map(role => role.id);
    const nextVersions = {};
    jobRoleCatalog.forEach(role => {
      nextVersions[role.id] = filterAssignable(role.versions).map(version => version.id);
    });
    setSelectedRoleIds(nextSelectedRoleIds);
    setSelectedVersionIdsByRole(nextVersions);
    setValidationErrors(current => ({ ...current, jobRoles: null }));
    await mergeSuggestedModules(collectVersionIds(nextSelectedRoleIds, nextVersions));
  };

  const handleAddModule = option => {
    if (!option?.value) {
      return;
    }
    const moduleId = option.value;
    setSelectedModuleIds(current => (current.includes(moduleId) ? current : [...current, moduleId]));
    setManualModuleIds(current => (current.includes(moduleId) ? current : [...current, moduleId]));
    setSelectedPartIdsByModule(current => ({
      ...current,
      [moduleId]: current[moduleId] || [],
    }));
    setValidationErrors(current => ({ ...current, trainingModules: null }));
  };

  const handleRemoveModule = moduleId => {
    if (requiredModuleIds.includes(moduleId)) {
      openErrorModal(
        t('company.cannotRemoveRequiredTrainingModule', {
          defaultValue: 'This training module is required by selected job role versions and cannot be removed',
        }),
      );
      return;
    }
    setSelectedModuleIds(current => current.filter(id => id !== moduleId));
    setManualModuleIds(current => current.filter(id => id !== moduleId));
    setSelectedPartIdsByModule(current => {
      const next = { ...current };
      delete next[moduleId];
      return next;
    });
    setValidationErrors(current => ({ ...current, trainingModules: null }));
  };

  const handleTogglePart = (moduleId, partId) => {
    setSelectedPartIdsByModule(current => ({
      ...current,
      [moduleId]: toggleId(current[moduleId] || [], partId),
    }));
    if (!requiredModuleIds.includes(moduleId)) {
      setManualModuleIds(current => (current.includes(moduleId) ? current : [...current, moduleId]));
    }
    setValidationErrors(current => ({ ...current, trainingModules: null }));
  };

  const handleAddAllModuleParts = () => {
    const nextSelected = Array.from(new Set([...selectedModuleIds, ...moduleCatalog.map(module => module.id)]));
    const nextParts = { ...selectedPartIdsByModule };
    moduleCatalog.forEach(module => {
      nextParts[module.id] = filterAssignable(module.parts).map(part => part.id);
    });
    setSelectedModuleIds(nextSelected);
    setSelectedPartIdsByModule(nextParts);
    setManualModuleIds(current =>
      Array.from(new Set([...current, ...nextSelected.filter(id => !requiredModuleIds.includes(id))])),
    );
    setValidationErrors(current => ({ ...current, trainingModules: null }));
  };

  const roleAddOptions = jobRoleCatalog
    .filter(role => !selectedRoleIds.includes(role.id))
    .map(role => ({ value: role.id, label: role.name }));

  if (loadState.loading && formKey === 0) {
    return <Loader />;
  }

  const busy = submitState.loading || loadState.loading || modulesLoading;
  const hasSaveErrors = Boolean(validationErrors.jobRoles || validationErrors.trainingModules);

  return (
    <Form
      key={formKey}
      onSubmit={saveAssignments}
      render={({ handleSubmit }) => (
        <form onSubmit={handleSubmit}>
          <Grid container className={classes.containerSubtitles}>
            <Grid className={classes.gridItem} item xs={6}>
              <Typography className={classes.subtitle} component="h2">
                {t('company.assignedJobRoles')}
              </Typography>
            </Grid>
            <Grid className={classes.gridItem} item xs={6}>
              <Typography className={classes.subtitle} component="h2">
                {t('company.assignedTrainingModules')}
              </Typography>
            </Grid>
          </Grid>

          <Grid container>
            <Grid className={classes.gridItem} item xs={6}>
              <Box mb={2}>
                <Field
                  name="addRole"
                  render={({ input, meta }) => (
                    <CustomSelect
                      input={{
                        ...input,
                        value: null,
                        onChange: option => {
                          handleAddRole(option);
                          input.onChange(null);
                        },
                      }}
                      meta={meta}
                      label={t('formLabels.jobsRoles')}
                      options={roleAddOptions}
                      placeholder={t('placeholders.chooseJobTask')}
                      isDisabled={busy}
                      isClearable
                    />
                  )}
                />
              </Box>
              <Box mb={2} display="flex" gridGap={8}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={handleAddAllRoleVersions}
                  disabled={busy || jobRoleCatalog.length === 0}
                >
                  {t('company.addAllRoles')}
                </Button>
              </Box>
              {validationErrors.jobRoles ? (
                <Typography color="error" variant="body2" style={{ marginBottom: 8 }}>
                  {validationErrors.jobRoles}
                </Typography>
              ) : null}
              {unselectedCatalogVersionsCount > 0 ? (
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 8 }}>
                  {t('company.unselectedCatalogVersionsHint', {
                    defaultValue:
                      '{{count}} global job role version(s) are not entitled yet. Select them here before company sync.',
                    count: unselectedCatalogVersionsCount,
                  })}
                </Typography>
              ) : null}
              <EntitlementAccordionList
                items={selectedRoles}
                disabled={busy}
                onRemove={handleRemoveRole}
                isIncomplete={role => !(selectedVersionIdsByRole[role.id] || []).length}
                getTitle={role => role.name}
                getMeta={role => {
                  const assignable = filterAssignable(role.versions);
                  const selectedCount = countSelectedAssignable(role.versions, selectedVersionIdsByRole[role.id]);
                  return t('company.versionsSelected', {
                    defaultValue: 'Versions — {{selected}}/{{total}} versions',
                    selected: selectedCount,
                    total: assignable.length,
                  });
                }}
                renderDetails={role => {
                  const assignableVersions = filterAssignable(role.versions);
                  return (
                    <Box>
                      {!(selectedVersionIdsByRole[role.id] || []).length ? (
                        <Typography color="error" variant="caption" display="block" gutterBottom>
                          {t('company.jobRoleVersionsRequired', {
                            defaultValue: 'At least one job role version is required',
                          })}
                        </Typography>
                      ) : null}
                      <VersionChipGroup
                        items={assignableVersions}
                        selectedIds={selectedVersionIdsByRole[role.id] || []}
                        disabled={busy}
                        getLabel={version => version.fullVersionCode || version.versionCode}
                        onToggle={versionId => handleToggleVersion(role.id, versionId)}
                      />
                    </Box>
                  );
                }}
              />
            </Grid>

            <Grid className={classes.gridItem} item xs={6}>
              <Box mb={2}>
                <Field
                  name="addModule"
                  render={({ input, meta }) => (
                    <CustomSelect
                      input={{
                        ...input,
                        value: null,
                        onChange: option => {
                          handleAddModule(option);
                          input.onChange(null);
                        },
                      }}
                      meta={meta}
                      label={t('trainingModules.pageTitle')}
                      options={addModuleOptions}
                      placeholder={
                        selectedVersionIds.length
                          ? t('trainingModules.searchPlaceholder')
                          : t('company.chooseJobRolesForTrainingModules')
                      }
                      isDisabled={busy}
                      isClearable
                    />
                  )}
                />
              </Box>
              <Box mb={2}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={handleAddAllModuleParts}
                  disabled={busy || moduleCatalog.length === 0}
                >
                  {t('company.addAllTrainingModules')}
                </Button>
              </Box>
              {validationErrors.trainingModules ? (
                <Typography color="error" variant="body2" style={{ marginBottom: 8 }}>
                  {validationErrors.trainingModules}
                </Typography>
              ) : null}
              {unselectedCatalogPartsCount > 0 ? (
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 8 }}>
                  {t('company.unselectedCatalogPartsHint', {
                    defaultValue:
                      '{{count}} global training part version(s) are not entitled yet. Select them here before company sync.',
                    count: unselectedCatalogPartsCount,
                  })}
                </Typography>
              ) : null}
              <EntitlementAccordionList
                items={selectedModules}
                disabled={busy}
                onRemove={handleRemoveModule}
                isIncomplete={module => !(selectedPartIdsByModule[module.id] || []).length}
                getTitle={module => `${module.code}. ${module.name}`}
                getMeta={module => {
                  const assignable = filterAssignable(module.parts);
                  const selectedCount = countSelectedAssignable(module.parts, selectedPartIdsByModule[module.id]);
                  const requiredLabel = requiredModuleIds.includes(module.id)
                    ? t('company.requiredByJobRoleVersion', {
                        defaultValue: 'required by job role version',
                      })
                    : null;
                  const counts = t('company.partVersionsSelected', {
                    defaultValue: '{{selected}}/{{total}} part-versions selected',
                    selected: selectedCount,
                    total: assignable.length,
                  });
                  return requiredLabel ? `${counts} · ${requiredLabel}` : counts;
                }}
                renderDetails={module => (
                  <Box>
                    {!(selectedPartIdsByModule[module.id] || []).length ? (
                      <Typography color="error" variant="caption" display="block" gutterBottom>
                        {t('company.trainingPartVersionsRequired', {
                          defaultValue: 'At least one part version is required',
                        })}
                      </Typography>
                    ) : null}
                    {TRAINING_METHODS.map(method => {
                      const methodParts = filterAssignable(module.parts).filter(part => part.trainingMethod === method);
                      if (!methodParts.length) {
                        return null;
                      }
                      const selectedCount = methodParts.filter(part =>
                        (selectedPartIdsByModule[module.id] || []).includes(part.id),
                      ).length;
                      return (
                        <Box key={method} mb={2}>
                          <VersionChipGroup
                            label={`${method} — ${selectedCount}/${methodParts.length}`}
                            items={methodParts}
                            selectedIds={selectedPartIdsByModule[module.id] || []}
                            disabled={busy}
                            getLabel={part => part.fullVersionCode || part.version}
                            onToggle={partId => handleTogglePart(module.id, partId)}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                )}
              />
            </Grid>
          </Grid>

          <Box ref={saveErrorsRef} mb={hasSaveErrors ? 2 : 0}>
            {validationErrors.jobRoles ? (
              <Typography color="error" variant="body2" style={{ marginBottom: 4 }}>
                {validationErrors.jobRoles}
              </Typography>
            ) : null}
            {validationErrors.trainingModules ? (
              <Typography color="error" variant="body2">
                {validationErrors.trainingModules}
              </Typography>
            ) : null}
          </Box>

          <Grid container spacing={3} className={classes.btnContainer}>
            <Grid item xs={6}>
              <Box textAlign="right">
                <Button
                  className={classes.btn}
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={busy}
                  size="large"
                >
                  {t('buttons.save')}
                </Button>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box textAlign="left">
                <Button
                  className={classes.btn}
                  variant="outlined"
                  size="large"
                  type="button"
                  disabled={busy}
                  onClick={() => history.push(routesAliases.companiesList)}
                >
                  {t('buttons.cancel')}
                </Button>
              </Box>
            </Grid>
          </Grid>

          {busy && <Loader />}
        </form>
      )}
    />
  );
};

RolesAndTrainingModulesForm.propTypes = {
  companyId: PropTypes.number.isRequired,
  routesAliases: PropTypes.shape({
    companiesList: PropTypes.string.isRequired,
  }).isRequired,
};

export default RolesAndTrainingModulesForm;
